import Collapsible from "react-collapsible";
import Select from "./components/Select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Spacer from "./components/Spacer";
import moment from "moment";
import Input from "./components/Input";
import { useCallback, useEffect, useMemo, useState } from "react";
import ConfirmModal from "../common/ConfirmModal";
import { LOCAL_PREFIX } from "../../utils/constants";
import { getHighestLevelWithPrefix } from "./utils/parseProblems";
import { levelToColor, levelToIconName } from "./components/ProblemIndicator";

const MANURE_OPTIONS = [
  "None",
  "Beef - Open Lot - Solid Manure",
  "Beef - Confinement Barn & Storage - Slurry",
  "Beef - Holding Pond - Liquid",
  "Beef - Holding Pond - Solids",
  "Beef - Open Lot -Compost",
  "Dairy - Confinement Barn - Solid Manure",
  "Dairy - Confinement Barn & Storage - Slurry",
  "Dairy - Confinement Barn & Lagoon - Liquid",
  "Dairy - Confinement Barn & Lagoon - Sludge",
  "Swine - Confinement Barn & Deep Pit - Slurry",
  "Swine - Confinement Barn & Lagoon - Liquid",
  "Swine - Confinement Barn & Lagoon - Sludge",
  "Layers - Confinement Barn - Scrapped",
  "Layers - Confinement Barn & Storage - Slurry",
  "Broilers - Confinement Barn - Litter",
  "Turkey - Confinement Barn - Litter",
];

const METHOD_OPTIONS = [
  "Applied in summer or fall, injected or incorporated immediately",
  "Applied in summer or fall, incorporated 1 day later",
  "Applied in summer or fall, incorporated 2 days later",
  "Applied in summer or fall, incorporated 3-7 days later",
  "Applied in summer or fall, not incorporated within one week",
  "Applied in winter or spring, injected or incorporated immediately",
  "Applied in winter or spring, incorporated 1 day later",
  "Applied in winter or spring, incorporated 2 days later",
  "Applied in winter or spring, incorporated 3-7 days later",
  "Applied in winter or spring, not incorporated within one week",
  "Sidedress, injection",
  "Irrigation, sprinkler",
  "Irrigation, flood",
];

function getConfirmMessage(toDelete) {
  if (toDelete.isSaved) {
    return `This will remove "${toDelete.name}". After saving you will not be able to recover this change. If unsaved, you can reload the page to recover this application.`;
  }

  return `This will permanently remove "${toDelete.name}". This cannot be undone.`;
}

export default function ManureApplicationSection({
  index = 0,
  crop,
  initialData,
  onRemove,
  onChange,
  formProblems,
}) {
  const [date, setDate] = useState(initialData.date ?? '');

  const [type, setType] = useState(initialData.type ?? '');
  const [ammoniumNitrogen, setAmmoniumNitrogen] = useState(initialData.ammoniumNitrogen ?? '');
  const [organicNitrogen, setOrganicNitrogen] = useState(initialData.organicNitrogen ?? '');
  const [amount, setAmount] = useState(initialData.amount ?? '');

  const [method, setMethod] = useState(initialData.method ?? '');

  const yearsAgoLabel = useMemo(() => {
    if (!date) return undefined;

    const yearsAgo = (new Date()).getFullYear() - date.getFullYear();
    if (yearsAgo === 0) return "(Current Year)";
    if (yearsAgo < 0) return "(Future Date)";
    return `(${yearsAgo} Years Ago)`;
  }, [date]);

  const [toDelete, setToDelete] = useState(undefined);

  useEffect(() => {
    onChange({
      id: initialData.id,
      date,
      type,
      ammoniumNitrogen,
      organicNitrogen,
      amount,
      method,
    });

  }, [date, type, ammoniumNitrogen, organicNitrogen, amount, method]);

  const setAndClearProblem = useCallback((setter, value, field) => {
    const i = formProblems.findIndex((p) => p.field === field);
    if (i >= 0) {
      formProblems.splice(i, 1);
    }

    setter(value);
  }, [formProblems]);

  const prefix = `${crop.id}-manure-${initialData.id}`;

  const problemLevel = getHighestLevelWithPrefix(formProblems, prefix, true);

  return (<>
    <Collapsible
      className="p-0.5 bg-slate-100 rounded-md w-full"
      openedClassName="p-0.5 bg-slate-100 rounded-md w-full"
      contentOuterClassName="w-full max-w-full"
      contentInnerClassName="flex flex-col px-2 pb-1"
      triggerClassName="group"
      triggerOpenedClassName="group"
      trigger={(
        <div className="flex items-center gap-2 pl-2">
          <FontAwesomeIcon
            icon="chevron-circle-down"
            className="group-[.is-closed]:-rotate-90 transition-all text-slate-500"
          />
          <h4>
            Manure Application {index + 1}
            {date && ` (${moment(date).format('MM/DD/YYYY')})`}
            {problemLevel !== "" && (
              <FontAwesomeIcon
                icon={levelToIconName(problemLevel)}
                className={`ml-1 ${levelToColor(problemLevel)}`}
                data-tooltip-id="invalid-application-tooltip"
              />
            )}
          </h4>
          <Spacer />
          <button
            title="Remove this application"
            className={`
              bg-red-500 text-white h-5 w-5 rounded-full flex items-center justify-center
              text-lg mr-4 group-[.is-closed]:hidden
            `}
            onClick={(event) => {
              event.stopPropagation();
              setToDelete({
                name: `Manure Application ${index + 1}`,
                confirm: onRemove,
                isSaved: !initialData.id.startsWith(LOCAL_PREFIX),
              });
            }}
          >
            -
          </button>
        </div>
      )}
      transitionTime={150}
      easing="ease-in"
      open
    >
      <Select
        label="Manure"
        name={`${prefix}-type`}
        formErrors={formProblems}
        value={type}
        options={MANURE_OPTIONS.map((v) => ({ label: v, value: v }))}
        onChange={({ value }) => setAndClearProblem(setType, value, `${prefix}-type`)}
      />
      <div className="flex gap-4">
        <Input
          label="Ammonium Nitrogen Amount"
          name={`${prefix}-amm-nitro`}
          formErrors={formProblems}
          type="number"
          suffix="lbs/ton"
          value={ammoniumNitrogen}
          onChange={(value) => setAndClearProblem(setAmmoniumNitrogen, value, `${prefix}-amm-nitro`)}
        />
        <Input
          label="Organic Nitrogen Amount"
          name={`${prefix}-org-nitro`}
          formErrors={formProblems}
          type="number"
          suffix="lbs/ton"
          value={organicNitrogen}
          onChange={(value) => setAndClearProblem(setOrganicNitrogen, value, `${prefix}-org-nitro`)}
        />
        <Input
          label="Amount"
          name={`${prefix}-amount`}
          formErrors={formProblems}
          type="number"
          suffix="Tons/acre"
          value={amount}
          onChange={(value) => setAndClearProblem(setAmount, value, `${prefix}-amount`)}
        />
      </div>
      <div className="flex gap-4">
        <Select
          label="Application Method"
          name={`${prefix}-method`}
          formErrors={formProblems}
          value={method}
          options={METHOD_OPTIONS.map((v) => ({ label: v, value: v }))}
          onChange={({ value }) => setAndClearProblem(setMethod, value, `${prefix}-method`)}
        />
        <Input
          label="Date Applied"
          name={`${prefix}-date`}
          formErrors={formProblems}
          description={yearsAgoLabel}
          type="date"
          value={date}
          onChange={(value) => setAndClearProblem(setDate, value, `${prefix}-date`)}
        />
      </div>
    </Collapsible>
    <ConfirmModal
      isOpen={!!toDelete}
      title="Confirm Delete Manure Application"
      message={toDelete ? getConfirmMessage(toDelete) : ""}
      confirmButtonLabel="Yes, Delete"
      toggle={() => setToDelete(undefined)}
      onConfirm={() => {
        toDelete.confirm();
        setToDelete(undefined);
      }}
      secondaryButtonText="Cancel"
      warning
    />
  </>);
}
